import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, ExternalLink } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { dec } from "@/lib/formatters";

export const HOUSEHOLD_TYPE_LABELS = {
  alle: "Alle hushald",
  aleneboende: "Einebuande",
  par_uten_barn: "Par utan barn",
  par_med_barn: "Par med barn 0-17 år",
  enslig_med_barn: "Ensleg mor/far med barn 0-17 år",
};
const TYPE_COLORS = { alle: "#1F2A36", aleneboende: "#D35E4E", par_uten_barn: "#3F8A86", par_med_barn: "#103766", enslig_med_barn: "#E8A34A" };

const pctDiff = (a, b) => (a && b ? `${a > b ? "+" : ""}${dec((((a - b) / b) * 100), 1)} %` : "—");

export const HouseholdIncomeCard = ({ income, fylke, land }) => {
  const rows = income || [];
  const latest = rows[rows.length - 1];
  if (!latest) return null;
  const fy = (fylke?.income || []).find((r) => r.year === latest.year);
  const la = (land?.income || []).find((r) => r.year === latest.year);
  const chartData = rows.map((r) => {
    const p = { year: r.year };
    Object.keys(HOUSEHOLD_TYPE_LABELS).forEach((k) => { p[HOUSEHOLD_TYPE_LABELS[k]] = r[k]?.etter_skatt ?? null; });
    return p;
  });

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="household-income-section">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Users className="w-5 h-5 text-[#103766]" />
          Inntekta til hushalda {latest.year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Median inntekt per hushaldstype – samla inntekt og inntekt etter skatt (kr)</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="overflow-x-auto">
          <Table data-testid="household-income-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-[#4B647F]">Hushaldstype</TableHead>
                <TableHead className="text-right text-[#4B647F]">Tal</TableHead>
                <TableHead className="text-right text-[#4B647F]">Samla inntekt</TableHead>
                <TableHead className="text-right text-[#4B647F]">Etter skatt</TableHead>
                {fy && <TableHead className="text-right text-[#4B647F]">Etter skatt – {fylke.name}</TableHead>}
                {la && <TableHead className="text-right text-[#4B647F]">Etter skatt – landet</TableHead>}
                {la && <TableHead className="text-right text-[#4B647F]">Avvik frå landet</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(HOUSEHOLD_TYPE_LABELS).map(([k, label]) => {
                const v = latest[k];
                if (!v) return null;
                return (
                  <TableRow key={k} data-testid={`household-income-${k}`} className={k === "alle" ? "font-medium bg-[#F8FAFC]" : ""}>
                    <TableCell className="text-[#1F2A36]">{label}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#4B647F]">{fmt(v.antall)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#1F2A36]">{fmt(v.samlet)} kr</TableCell>
                    <TableCell className="text-right tabular-nums text-[#1F2A36]">{fmt(v.etter_skatt)} kr</TableCell>
                    {fy && <TableCell className="text-right tabular-nums text-[#4B647F]">{fy[k]?.etter_skatt ? `${fmt(fy[k].etter_skatt)} kr` : "—"}</TableCell>}
                    {la && <TableCell className="text-right tabular-nums text-[#4B647F]">{la[k]?.etter_skatt ? `${fmt(la[k].etter_skatt)} kr` : "—"}</TableCell>}
                    {la && <TableCell className={`text-right tabular-nums ${v.etter_skatt >= (la[k]?.etter_skatt || 0) ? "text-[#3F8A86]" : "text-[#D35E4E]"}`}>{pctDiff(v.etter_skatt, la[k]?.etter_skatt)}</TableCell>}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">Median inntekt etter skatt per hushaldstype (kr)</p>
          <div className="h-[300px]" data-testid="household-income-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                {endringsLinjer(chartData)}
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `${fmt(v)} kr`} contentStyle={tooltipStyle} itemSorter={(i) => -i.value} />
                <Legend itemSorter={null} />
                {Object.entries(HOUSEHOLD_TYPE_LABELS).map(([k, label]) => (
                  <Line key={k} type="monotone" dataKey={label} stroke={TYPE_COLORS[k]} strokeWidth={k === "alle" ? 3 : 2} dot={{ fill: TYPE_COLORS[k], r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <p className="text-xs text-[#8AA4BF] flex items-center gap-1">
          Kjelde:
          <a href="https://www.ssb.no/statbank/table/06944" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#103766] hover:underline">
            SSB 06944 <ExternalLink className="w-3 h-3" />
          </a>
          – Inntekt for hushald, etter hushaldstype. Studenthushald er ikkje inkludert.
        </p>
      </CardContent>
    </Card>
  );
};
